import {
  BookOpen, Server, Terminal, ShieldCheck, Download, FileText, Layers, Cloud, KeyRound,
  Lock, Eye, Scale, Activity, FileCheck2, Users, Newspaper, Image, Package, GraduationCap,
  Landmark, Cpu, GitBranch, Boxes, ScrollText, Globe2,
  MonitorPlay, CalendarClock, Handshake, LifeBuoy, Headphones,
} from "lucide-react";

// --------------------------------------------------------------------------- //
// DOCUMENTATION CENTER
// --------------------------------------------------------------------------- //
export const documentation = {
  path: "/documentation",
  seoTitle: "Documentation Center",
  seoDescription: "Technical documentation, architecture, deployment guides, administration and API references for AstroLife™ AI Governance OS.",
  overline: "Documentation Center",
  title: "Technical documentation for the AI Governance OS.",
  subtitle: "Architecture references, deployment guides, administration and API documentation for teams operating verifiable AI governance at scale.",
  breadcrumbs: [{ label: "Documentation" }],
  blocks: [
    {
      type: "linkcards", overline: "Guides", title: "Everything your teams need to deploy and operate.",
      items: [
        { icon: Layers, title: "Architecture", text: "System architecture, data model and the governance evidence chain.", cta: "Read architecture" },
        { icon: Cloud, title: "Deployment Guides", text: "On-premise, sovereign-cloud, private-cloud and hybrid deployment paths.", cta: "View guides" },
        { icon: ShieldCheck, title: "Administration Guide", text: "User management, roles, policies, controls libraries and configuration.", cta: "Open guide" },
        { icon: BookOpen, title: "User Guide", text: "Day-to-day workflows for inventory, risk, lifecycle and audit teams.", cta: "Open guide" },
        { icon: Terminal, title: "API Documentation", text: "REST API references for integration with CRM, SIEM and data platforms.", cta: "Explore API" },
        { icon: Download, title: "Downloads", text: "Reference profiles, control templates and release artefacts.", cta: "Downloads" },
      ],
    },
    {
      type: "steps", overline: "Deployment", title: "A guided path to production.",
      items: [
        { title: "Provision", text: "Choose on-premise, sovereign, private or hybrid; align to data-residency requirements." },
        { title: "Configure", text: "Set up authentication (Keycloak / Entra ID / Auth0 / Okta), roles and policies." },
        { title: "Inventory", text: "Onboard AI systems into the AI-BOM and classify risk tiers." },
        { title: "Operate", text: "Run governance workflows, generate evidence and monitor posture continuously." },
      ],
    },
    {
      type: "accordion", overline: "FAQ", title: "Documentation FAQ",
      items: [
        { q: "Where do I start?", a: "Begin with the Architecture reference to understand the governance model, then follow the Deployment Guide for your target environment." },
        { q: "Is there an API?", a: "Yes. A REST API supports inventory, risk, evidence and reporting operations, enabling integration with existing enterprise systems." },
        { q: "How are updates delivered?", a: "Release Notes document each version, including new controls, regulatory profiles and platform capabilities." },
      ],
    },
  ],
  cta: true,
};

// --------------------------------------------------------------------------- //
// TRUST CENTER
// --------------------------------------------------------------------------- //
export const trustCenter = {
  path: "/trust-center",
  seoTitle: "Trust Center — Security, Privacy & Responsible AI",
  seoDescription: "AstroLife™ Trust Center: security, privacy, compliance, governance, responsible AI, auditability, traceability, explainability and transparency.",
  overline: "Trust Center",
  title: "Security, privacy and responsible AI by design.",
  subtitle: "Trust is engineered, not declared. The AstroLife Trust Center consolidates how we protect data, govern AI and demonstrate accountability.",
  breadcrumbs: [{ label: "Trust Center" }],
  blocks: [
    {
      type: "grid", overline: "Foundations", title: "How we earn and demonstrate trust.",
      items: [
        { icon: Lock, title: "Security", text: "Zero-trust access, encryption in transit and at rest, and hardened deployments." },
        { icon: KeyRound, title: "Privacy", text: "Data minimisation, residency controls and GDPR-aligned processing." },
        { icon: Scale, title: "Compliance", text: "Support for the EU AI Act, ISO/IEC 42001, ISO/IEC 27001 and NIST AI RMF." },
        { icon: ShieldCheck, title: "Governance", text: "Repeatable controls, clear ownership and measurable posture." },
        { icon: Cpu, title: "Responsible AI", text: "Human oversight, fairness and accountability embedded across the lifecycle." },
        { icon: Activity, title: "Risk Management", text: "Persistent risk registers with classification, mitigation and monitoring." },
        { icon: FileCheck2, title: "Auditability", text: "Actions, approvals and evidence form a durable, reviewable record." },
        { icon: GitBranch, title: "Traceability", text: "Provenance from data to model to decision — end to end." },
        { icon: Eye, title: "Explainability & Transparency", text: "Explainability artefacts and human-oversight records for high-impact AI." },
      ],
    },
    {
      type: "intro",
      paragraphs: [
        "Every control in the AstroLife AI Governance OS is linked to durable, tamper-evident evidence. This turns operational activity into a defensible record for internal reviews, external audits and regulators.",
        "The platform provides tooling support aligned with leading frameworks and standards. It does not constitute legal advice; obligations remain the responsibility of the operating organisation.",
      ],
    },
  ],
  cta: true,
};

// --------------------------------------------------------------------------- //
// KNOWLEDGE CENTER (Glossary + FAQ + resources)
// --------------------------------------------------------------------------- //
export const knowledge = {
  path: "/knowledge",
  seoTitle: "Knowledge Center — Glossary, FAQ & Governance Guides",
  seoDescription: "AstroLife™ Knowledge Center: AI governance glossary, frequently asked questions, EU AI Act resources and learning guides.",
  overline: "Knowledge Center",
  title: "Understand verifiable AI governance.",
  subtitle: "A glossary, frequently asked questions and governance guides to help teams operationalise trustworthy artificial intelligence.",
  breadcrumbs: [{ label: "Knowledge Center" }],
  blocks: [
    {
      type: "definitions", overline: "Glossary", title: "Key terms in AI governance.",
      items: [
        { term: "AI Governance", def: "The processes, controls and accountability structures used to manage AI responsibly across its lifecycle." },
        { term: "AI-BOM", def: "AI Bill of Materials — a versioned register of every model, dataset, dependency and control." },
        { term: "Verifiable AI", def: "AI whose behaviour, controls and evidence can be independently reviewed and demonstrated." },
        { term: "Risk Register", def: "A persistent record of identified AI risks with classification, ownership and mitigation status." },
        { term: "Explainability", def: "Artefacts and records that make an AI system's decisions understandable and defensible." },
        { term: "Human Oversight", def: "A first-class governance control ensuring meaningful human control over high-impact AI." },
        { term: "Immutable Audit Trail", def: "Tamper-evident logging of actions, approvals and evidence." },
        { term: "Sovereign AI", def: "AI infrastructure aligned with a jurisdiction's values, data residency and control requirements." },
      ],
    },
    {
      type: "accordion", overline: "FAQ", title: "Frequently asked questions.",
      items: [
        { q: "What is AI governance?", a: "AI governance is the set of processes, controls and accountability structures that organisations use to manage artificial intelligence responsibly across its lifecycle — covering inventory, risk, transparency, human oversight, evidence and compliance." },
        { q: "How does AstroLife support the EU AI Act?", a: "The platform helps teams inventory AI systems, classify risk, map obligations to a timeline, track requirements and maintain the documentation and evidence needed for accountability. It provides tooling support and does not constitute legal advice." },
        { q: "Can it be deployed on-premise?", a: "Yes — on-premise, sovereign cloud, private cloud or hybrid, to meet data residency, sovereignty and security requirements." },
        { q: "Which frameworks does it align with?", a: "The EU AI Act, ISO/IEC 42001, ISO/IEC 27001 and the NIST AI Risk Management Framework, extensible via versioned regulatory profiles." },
      ],
    },
    {
      type: "linkcards", overline: "Learning", title: "Governance guides & resources.",
      items: [
        { icon: BookOpen, title: "Governance Guides", text: "Practical playbooks for operationalising AI governance controls.", to: "/research" },
        { icon: Scale, title: "AI Act Resources", text: "Understand obligations, timelines and evidence expectations.", to: "/regulation" },
        { icon: FileText, title: "White Papers", text: "Reference frameworks and technical publications.", to: "/research" },
      ],
    },
  ],
  cta: true,
};

// --------------------------------------------------------------------------- //
// MEDIA CENTER
// --------------------------------------------------------------------------- //
export const media = {
  path: "/media",
  seoTitle: "Media Center — News, Press & Brand Assets",
  seoDescription: "AstroLife™ Media Center: news, press releases, media kit and brand assets.",
  overline: "Media Center",
  title: "News, press and brand resources.",
  subtitle: "Official announcements, press materials and brand assets for journalists, partners and institutions.",
  breadcrumbs: [{ label: "Media Center" }],
  blocks: [
    {
      type: "linkcards", overline: "Newsroom", title: "Latest institutional news.",
      items: [
        { icon: Newspaper, title: "News", text: "Company updates, milestones and institutional announcements.", to: "/blog?category=Company News" },
        { icon: FileText, title: "Press Releases", text: "Official press releases and statements.", to: "/blog?category=Company News" },
        { icon: Package, title: "Media Kit", text: "Company overview, fact sheet and executive information.", cta: "Request kit", to: "/contact" },
      ],
    },
    {
      type: "linkcards", overline: "Brand Assets", title: "Logos and imagery.",
      items: [
        { icon: Image, title: "Logos", text: "The AstroLife emblem and wordmark in approved formats.", cta: "Request assets", to: "/contact" },
        { icon: Image, title: "Images", text: "Approved imagery for editorial and partner use.", cta: "Request assets", to: "/contact" },
        { icon: Globe2, title: "Brand Guidelines", text: "Correct usage of the AstroLife identity.", cta: "Request guidelines", to: "/contact" },
      ],
    },
  ],
  cta: true,
};

// --------------------------------------------------------------------------- //
// EUROPEAN REGULATION HUB
// --------------------------------------------------------------------------- //
export const regulation = {
  path: "/regulation",
  seoTitle: "European Regulation — EU AI Act, GDPR, NIS2 & Standards",
  seoDescription: "Institutional overview of European regulation and standards: EU AI Act, GDPR, NIS2, Cyber Resilience Act, Data Act, ISO/IEC 42001, ISO/IEC 23894, NIST AI RMF, ENISA, EuroHPC and GAIA-X.",
  overline: "European Regulation",
  title: "Navigating Europe's AI and digital regulation.",
  subtitle: "How AstroLife supports readiness across the European regulatory and standards landscape. Tooling support only — not legal advice.",
  breadcrumbs: [{ label: "Regulation" }],
  blocks: [
    {
      type: "grid", overline: "Regulations", title: "The European regulatory landscape.",
      items: [
        { icon: Scale, title: "EU AI Act", text: "Risk-based obligations for AI systems; classification, documentation and evidence." },
        { icon: KeyRound, title: "GDPR", text: "Data protection and privacy for personal data processing." },
        { icon: ShieldCheck, title: "NIS2", text: "Cybersecurity obligations for essential and important entities." },
        { icon: Lock, title: "Cyber Resilience Act", text: "Security requirements for products with digital elements." },
        { icon: Boxes, title: "Data Act", text: "Fair access to and use of data across the economy." },
        { icon: ScrollText, title: "Data Governance Act", text: "Trusted data sharing and re-use frameworks." },
      ],
    },
    {
      type: "grid", overline: "Standards & Bodies", title: "Standards and institutions we align with.",
      items: [
        { icon: FileCheck2, title: "ISO/IEC 42001", text: "AI management system with controls and continual improvement." },
        { icon: Activity, title: "ISO/IEC 23894", text: "Guidance on AI risk management." },
        { icon: Cpu, title: "NIST AI RMF", text: "Govern, Map, Measure and Manage functions for AI risk." },
        { icon: ShieldCheck, title: "ENISA", text: "European cybersecurity guidance and good practice." },
        { icon: Server, title: "EuroHPC", text: "European high-performance computing capability." },
        { icon: Globe2, title: "GAIA-X", text: "Federated, sovereign European data infrastructure." },
      ],
    },
  ],
  cta: true,
};

// --------------------------------------------------------------------------- //
// PRODUCT COMPARISON
// --------------------------------------------------------------------------- //
export const comparison = {
  path: "/comparison",
  seoTitle: "Why AstroLife — Capability Comparison",
  seoDescription: "How the AstroLife™ AI Governance OS compares to generic AI tooling on auditability, verifiable AI, sovereign deployment, evidence generation and continuous compliance.",
  overline: "Why AstroLife",
  title: "Engineered for verifiable, sovereign governance.",
  subtitle: "A capability comparison between the AstroLife AI Governance OS and generic market approaches. Governance you can prove — not merely declare.",
  breadcrumbs: [{ label: "Comparison" }],
  blocks: [
    {
      type: "comparison", overline: "Capabilities", title: "A different class of AI governance.",
      us: "AstroLife AI Governance OS", them: "Generic solutions",
      rows: [
        { capability: "Verifiable, evidence-backed governance", us: true, them: "Partial" },
        { capability: "Immutable, tamper-evident audit trail", us: true, them: false },
        { capability: "Sovereign & on-premise deployment", us: true, them: "Limited" },
        { capability: "AI Bill of Materials (AI-BOM) inventory", us: true, them: "Partial" },
        { capability: "Evidence generation as a by-product of operations", us: true, them: false },
        { capability: "Operational governance workflows with ownership", us: true, them: "Partial" },
        { capability: "Continuous compliance as regulations evolve", us: true, them: false },
        { capability: "Executive governance & reporting", us: true, them: "Limited" },
        { capability: "EU AI Act obligation mapping & timelines", us: true, them: "Partial" },
        { capability: "Zero-trust access model", us: true, them: "Limited" },
      ],
    },
    {
      type: "intro",
      paragraphs: [
        "Generic tooling can catalogue models or track tickets. Verifiable AI governance requires more: a defensible, continuous chain of evidence linking every control and decision to a durable record.",
        "AstroLife is engineered for the organisations where trust is non-negotiable — governments, critical infrastructure and regulated industries operating under European digital sovereignty requirements.",
      ],
    },
  ],
  cta: true,
};

// --------------------------------------------------------------------------- //
// ENTERPRISE
// --------------------------------------------------------------------------- //
export const enterprise = {
  path: "/enterprise",
  seoTitle: "Enterprise — Demo, Partnership & Support",
  seoDescription: "Enterprise engagement for AstroLife™ AI Governance OS: request a demo, book a meeting, partner programs, customer success and support.",
  overline: "Enterprise",
  title: "Partner with AstroLife.",
  subtitle: "Enterprise engagement for governments, critical infrastructure and regulated industries — from first demo to production and long-term success.",
  breadcrumbs: [{ label: "Enterprise" }],
  blocks: [
    {
      type: "linkcards", overline: "Get Started", title: "Engage with our team.",
      items: [
        { icon: MonitorPlay, title: "Request Demo", text: "See the AI Governance OS applied to your governance objectives.", to: "/private-beta", cta: "Request demo" },
        { icon: CalendarClock, title: "Book a Meeting", text: "Arrange a briefing with our engineering and governance team.", to: "/contact", cta: "Book meeting" },
        { icon: Users, title: "Enterprise Contact", text: "Speak with us about deployment, procurement and sovereignty.", to: "/contact", cta: "Contact" },
      ],
    },
    {
      type: "linkcards", overline: "Programs", title: "Partnership & success.",
      items: [
        { icon: Handshake, title: "Partner Program", text: "Technology, research and institutional partnerships.", to: "/contact", cta: "Become a partner" },
        { icon: LifeBuoy, title: "Customer Success", text: "Onboarding, enablement and ongoing governance maturity.", to: "/contact", cta: "Learn more" },
        { icon: Headphones, title: "Support", text: "Enterprise support for production deployments.", to: "/contact", cta: "Get support" },
      ],
    },
  ],
  cta: true,
};

// --------------------------------------------------------------------------- //
// DOWNLOAD CENTER
// --------------------------------------------------------------------------- //
export const downloads = {
  path: "/downloads",
  seoTitle: "Download Center — White Papers, Datasheets & Brochures",
  seoDescription: "Download AstroLife™ white papers, datasheets, architecture diagrams, brochures and case studies (access on request).",
  overline: "Download Center",
  title: "Resources for evaluation and procurement.",
  subtitle: "White papers, datasheets, architecture diagrams and brochures to support technical evaluation and institutional procurement.",
  breadcrumbs: [{ label: "Downloads" }],
  blocks: [
    {
      type: "linkcards", overline: "Documents", title: "Available on request.",
      items: [
        { icon: FileText, title: "White Papers", text: "Reference frameworks and technical publications.", to: "/research", cta: "Browse" },
        { icon: FileText, title: "Datasheets", text: "Platform capabilities and deployment specifications.", to: "/contact", cta: "Request" },
        { icon: Layers, title: "Architecture Diagrams", text: "Reference architectures for sovereign deployment.", to: "/contact", cta: "Request" },
        { icon: Newspaper, title: "Brochures", text: "Executive overview of the AI Governance OS.", to: "/contact", cta: "Request" },
        { icon: BookOpen, title: "Case Studies", text: "Illustrative governance outcomes by sector.", to: "/research", cta: "Browse" },
        { icon: Package, title: "Media Kit", text: "Company overview and brand assets.", to: "/media", cta: "Open" },
      ],
    },
  ],
  cta: true,
};

export const institutionalPages = { documentation, trustCenter, knowledge, media, regulation, comparison, enterprise, downloads };
export const ecosystem = [
  { label: "AstroLife™", to: "/", icon: Globe2 },
  { label: "AI Governance OS", to: "/ai-governance-os", icon: ShieldCheck },
  { label: "Academy", href: "#", icon: GraduationCap, soon: true },
  { label: "Research Hub", to: "/research", icon: BookOpen },
  { label: "Developers", href: "#", icon: Terminal, soon: true },
  { label: "Documentation", to: "/documentation", icon: FileText },
  { label: "Support", to: "/contact", icon: Users },
];
